import React, { useState } from 'react';
import { ZegoUIKitPrebuilt } from '@zegocloud/zego-uikit-prebuilt';
import { v4 as uuidv4 } from 'uuid';
import { Button, Form, Container, Row, Col, Card, Alert } from 'react-bootstrap';

/**
 * Test Meeting Generator
 *
 * PURPOSE: Generate instant test meeting links for cross-device testing
 * without requiring backend API calls or database writes.
 *
 * USAGE:
 * 1. Click "Generate New Test Meeting"
 * 2. Copy Host Link and paste into LambdaTest Device 1
 * 3. Copy Guest Link and paste into LambdaTest Device 2
 * 4. Both devices join same ZegoCloud room automatically
 * 5. Test bidirectional audio/video
 *
 * PERFECT FOR: LambdaTest Real Device testing, Playwright automation
 */
export default function TestMeetingGenerator() {
  const [roomID, setRoomID] = useState('');
  const [hostURL, setHostURL] = useState('');
  const [guestURL, setGuestURL] = useState('');
  const [generated, setGenerated] = useState(false);
  const [copied, setCopied] = useState('');

  const appID = process.env.REACT_APP_ZEGO_CLOUD_APP_ID;
  const serverSecret = process.env.REACT_APP_ZEGO_CLOUD_SERVER_SECRET;

  const generateTestMeeting = () => {
    // Generate unique room ID
    const newRoomID = `test-${uuidv4()}`;

    // Generate tokens for host
    const hostID = `host-${Date.now()}`;
    const hostName = 'Test Host';
    const tokenHost = ZegoUIKitPrebuilt.generateKitTokenForTest(
      parseInt(appID),
      serverSecret,
      newRoomID,
      hostID,
      hostName
    );

    // Generate tokens for guest
    const guestID = `guest-${Date.now()}`;
    const guestName = 'Test Guest';
    const tokenGuest = ZegoUIKitPrebuilt.generateKitTokenForTest(
      parseInt(appID),
      serverSecret,
      newRoomID,
      guestID,
      guestName
    );

    // Generate URLs with tokens embedded as query params
    const baseURL = window.location.origin;
    const hostLink = `${baseURL}/test-meeting?roomID=${newRoomID}&token=${encodeURIComponent(tokenHost)}&userName=${encodeURIComponent(hostName)}&userID=${hostID}&role=host`;
    const guestLink = `${baseURL}/test-meeting?roomID=${newRoomID}&token=${encodeURIComponent(tokenGuest)}&userName=${encodeURIComponent(guestName)}&userID=${guestID}&role=guest`;

    setRoomID(newRoomID);
    setHostURL(hostLink);
    setGuestURL(guestLink);
    setGenerated(true);
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert(`${label} copied!`);
    }
  };

  return (
    <Container className="mt-5" style={{ maxWidth: '900px' }}>
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h3 className="mb-0">🧪 Test Meeting Generator</h3>
              <small>Generate instant meeting links for cross-device testing</small>
            </Card.Header>
            <Card.Body>
              <Alert variant="info">
                <strong>🎯 Purpose:</strong> Create test meetings without backend API calls.
                Use these links on different devices/browsers via LambdaTest to diagnose video issues.
              </Alert>

              <Button
                variant="success"
                size="lg"
                onClick={generateTestMeeting}
                className="w-100 mb-4"
                style={{ fontSize: '1.2rem', padding: '15px' }}
              >
                🎬 Generate New Test Meeting
              </Button>

              {generated && (
                <>
                  <Alert variant="success">
                    <strong>✅ Test Meeting Generated!</strong>
                    <br />
                    <small>Room ID: <code>{roomID}</code></small>
                  </Alert>

                  <div className="mb-4 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                    <h5 className="mb-3">
                      <span style={{ fontSize: '1.5rem' }}>🎯</span> Real Customer Link
                    </h5>
                    <Form.Group className="mb-2">
                      <Form.Control
                        as="textarea"
                        rows={3}
                        readOnly
                        value={hostURL}
                        style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}
                      />
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button
                        variant="primary"
                        onClick={() => copyToClipboard(hostURL, 'Host')}
                        className="flex-grow-1"
                      >
                        {copied === 'Host' ? '✅ Copied!' : '📋 Copy Host Link'}
                      </Button>
                      <Button
                        variant="outline-primary"
                        onClick={() => window.open(hostURL, '_blank')}
                      >
                        🔗 Open
                      </Button>
                    </div>
                  </div>

                  <div className="mb-4 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                    <h5 className="mb-3">
                      <span style={{ fontSize: '1.5rem' }}>👤</span> Guest Link
                    </h5>
                    <Form.Group className="mb-2">
                      <Form.Control
                        as="textarea"
                        rows={3}
                        readOnly
                        value={guestURL}
                        style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}
                      />
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button
                        variant="info"
                        onClick={() => copyToClipboard(guestURL, 'Guest')}
                        className="flex-grow-1"
                      >
                        {copied === 'Guest' ? '✅ Copied!' : '📋 Copy Guest Link'}
                      </Button>
                      <Button
                        variant="outline-info"
                        onClick={() => window.open(guestURL, '_blank')}
                      >
                        🔗 Open
                      </Button>
                    </div>
                  </div>

                  <Alert variant="warning">
                    <strong>📋 LambdaTest Testing Steps:</strong>
                    <ol className="mb-0 mt-2" style={{ fontSize: '0.95rem' }}>
                      <li>Login to <a href="https://www.lambdatest.com/real-device-testing" target="_blank" rel="noopener noreferrer">LambdaTest Real Devices</a></li>
                      <li>Device 1: Select iPhone 15 Pro (iOS 17, Safari)</li>
                      <li>Paste <strong>Real Customer Link</strong> into device browser</li>
                      <li>Grant camera/microphone permissions</li>
                      <li>Device 2: Select Samsung Galaxy S23 (Android 13, Chrome)</li>
                      <li>Paste <strong>Guest Link</strong> into device browser</li>
                      <li>Grant camera/microphone permissions</li>
                      <li>Both should see/hear each other!</li>
                      <li>Check console logs on both devices for errors</li>
                      <li>Test network throttling, screen sharing, etc.</li>
                    </ol>
                  </Alert>

                  <Alert variant="secondary">
                    <strong>ℹ️ How This Works:</strong>
                    <ul className="mb-0 mt-2" style={{ fontSize: '0.9rem' }}>
                      <li>Generates unique room ID (UUID with "test-" prefix)</li>
                      <li>Creates ZegoCloud tokens for both participants</li>
                      <li>Tokens include: room ID, user ID, user name, role</li>
                      <li><strong>No backend API calls</strong> - pure client-side</li>
                      <li><strong>No database writes</strong> - nothing persisted</li>
                      <li>Tokens valid for 10 hours (ZegoCloud default)</li>
                      <li>Meetings expire automatically - no cleanup needed</li>
                    </ul>
                  </Alert>

                  <div className="text-center mt-4">
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setGenerated(false);
                        setRoomID('');
                        setHostURL('');
                        setGuestURL('');
                      }}
                    >
                      🔄 Generate Another Meeting
                    </Button>
                  </div>
                </>
              )}

              {!generated && (
                <Alert variant="light" className="mt-3">
                  <strong>💡 Pro Tips:</strong>
                  <ul className="mb-0 mt-2" style={{ fontSize: '0.9rem' }}>
                    <li>Each generated meeting is completely independent</li>
                    <li>Safe to test on production - no database impact</li>
                    <li>Can open host/guest links in same browser for quick local testing</li>
                    <li>Perfect for reproducing user-reported issues on specific devices</li>
                    <li>Combine with LambdaTest video recording for bug reports</li>
                  </ul>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
